import React, { useCallback, useState } from 'react';

import { makeStyles } from '@material-ui/core';
import { TFunction } from 'i18next';
import { defaultTo, isNil } from 'ramda';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import { DialogProps } from '@mui/material/Dialog';
import {
  DataGridProProps,
  GridRowId,
  GridValidRowModel
} from '@mui/x-data-grid-pro';

import { Dialog, FontAwesomeSvgIcon } from 'components/shared';
import { faFolder } from '@fortawesome/pro-light-svg-icons';

import { DataGrid } from '../DataGrid';
import {
  CheckBoxSelectionProps,
  CustomDataGridProps,
  DataGridAction,
  DataGridActionsFn
} from '../DataGrid/DataGrid';
import { CustomDrawerProps } from '../Drawer/Drawer';
import { LoadingError } from '../LoadingError/LoadingError';
import { Drawer } from '../index';
import { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';

export interface FormComponentProps<T = object> {
  rowId: number | string | undefined;
  rowData?: T | undefined;
  rows?: T[];
  handleClose: (props?: any) => void;
}

export type TableCRUDLayoutProps<T extends GridValidRowModel = any> = Omit<
  CustomDataGridProps<T>,
  'rows'
> & {
  FormComponent?: React.FC<FormComponentProps<T> & any>;
  formContainer?: 'drawer' | 'dialog';
  handleDelete?: (id: GridRowId[]) => void;
  drawerProps?: Partial<CustomDrawerProps>;
  drawerWidth?: number;
  rows?: DataGridProProps<T>['rows'];
  dialogProps?: Partial<DialogProps>;
  onLoadingError?: (props?: any) => unknown;
  hasLoadingError?: boolean | undefined;
  hideCreateAction?: boolean;
  onClose?: (props?: any) => any;
  editable?: boolean;
  customEditTitle?: string;
  customCreateTitle?: string;
  additionalFormProps?: Record<string, any>;
  onAddClick?: (rowSetter: () => void) => void;
  collapse?: number;
  hideDescendantCount?: boolean;
  isContentRangePagination?: boolean;
  page?: number;
  pageSize?: number;
  rowCount?: number;
  onPageChange?: (newPage: number, details: any) => void;
  onPageSizeChange?: (newPageSize: number, details: any) => void;
  initialState?: GridInitialStatePro | undefined;
};

export interface TableCRUDLayoutHandlers<T extends GridValidRowModel = any> {
  /**
   * Closes the container (drawer/dialog)
   */
  handleClose: () => void;
  /**
   * Opens the container (drawer/dialog)
   */
  handleOpen: () => void;
  /**
   * Opens the container (drawer/dialog) and sets the state of id and rowData
   */
  handleEditClick?: (id: number | string, rowData: T) => void;
}

const useStyles = makeStyles(() => ({
  createButtonContainer: { display: 'flex', justifyContent: 'flex-end' },
  dataGridContainer: (props: { autoHeight: boolean }) => ({
    height: !props.autoHeight ? 'calc(100% - 35px)' : 'auto',
    width: '100%'
  })
}));

const createTitle = (
  rowId: number | string | undefined,
  t: TFunction,
  customEditTitle?: string,
  customCreateTitle?: string
): string => {
  if (isNil(rowId)) {
    return customCreateTitle ?? t('new');
  }

  return customEditTitle ?? t('edit');
};

/**
 * This component is a wrapper for the commonly used table CRUD layout.
 * It renders the shared DataGrid, the shared Drawer or Dialog for form handling and also handles the create/edit part.
 */
const TableCRUDLayout = <T extends GridValidRowModel = any>({
  FormComponent,
  additionalFormProps,
  rows,
  columns,
  collapse,
  drawerProps,
  drawerWidth,
  dialogProps,
  hasLoadingError,
  onLoadingError,
  hideCreateAction,
  canBeEditedProp,
  canBeDeletedProp,
  onClose,
  editable = true,
  customActions,
  customEditTitle,
  customCreateTitle,
  formContainer = 'drawer',
  autoHeight = false,
  onAddClick,
  shouldRenderToolBar = true,
  hideDescendantCount,
  disableActions,
  isContentRangePagination,
  page = 0,
  pageSize = 50,
  rowCount = 0,
  onPageChange,
  onPageSizeChange,
  initialState,
  ...props
}: TableCRUDLayoutProps<T> & CheckBoxSelectionProps) => {
  const [containerOpen, setContainerOpen] = useState(false);
  const [rowId, setRowId] = useState(undefined);
  const [rowData, setRowData] = useState(undefined);
  const { t } = useTranslation();
  const classes = useStyles({ autoHeight });

  const handleClose = () => {
    setContainerOpen(false);

    onClose && onClose(rowData);
  };

  const onExited = () => {
    setRowId(undefined);
    setRowData(undefined);
  };

  const handleEditClick = (id, rowData) => {
    setRowId(id);
    setRowData(rowData);
    setContainerOpen(true);
  };

  const handleOpen = () => setContainerOpen(true);

  const handleAddClick = () => {
    onAddClick && onAddClick();
    handleOpen();
  };

  const getCustomActions = useCallback<DataGridActionsFn<T>>(
    (id: string | number, row?: T): DataGridAction<T>[] => {
      const actions =
        typeof customActions === 'function'
          ? customActions(id, row)
          : customActions ?? [];

      return actions.map((customAction) => ({
        ...customAction,
        onClick: (id, row) => {
          // pass the handlers to the action
          customAction.onClick(id, row, {
            handleOpen,
            handleEditClick,
            handleClose
          } as TableCRUDLayoutHandlers<T>);
        }
      }));
    },
    [customActions]
  );

  const groupingColDef: DataGridProProps['groupingColDef'] = {
    headerName: t('name'),
    valueGetter: (params) => {
      if (params.rowNode.children) {
        return (
          <>
            <FontAwesomeSvgIcon
              icon={faFolder}
              fontSize="small"
              sx={{ mr: '8px', verticalAlign: 'sub' }}
              color="primary"
            />
            {params.row.name}
          </>
        );
      } else {
        return params.row.name ? params.row.name : params.row[0].name;
      }
    },
    minWidth: 300,
    hideDescendantCount: hideDescendantCount
  };

  return (
    <>
      {hasLoadingError ? (
        <LoadingError {...(onLoadingError && { reFetch: onLoadingError })} />
      ) : (
        <>
          {formContainer === 'drawer' && (
            <Drawer
              {...drawerProps}
              open={containerOpen}
              onClose={handleClose}
              title={createTitle(rowId, t, customEditTitle, customCreateTitle)}
              SlideProps={{ onExited: onExited }}
            >
              <Box sx={{ width: drawerWidth ? drawerWidth : 400 }}>
                {FormComponent && (
                  <FormComponent
                    rowId={rowId}
                    rowData={rowData}
                    rows={rows}
                    handleClose={handleClose}
                    {...additionalFormProps}
                  />
                )}
              </Box>
            </Drawer>
          )}
          {formContainer === 'dialog' && (
            <Dialog
              open={containerOpen}
              title={createTitle(rowId, t, customEditTitle, customCreateTitle)}
              handleClose={handleClose}
              TransitionProps={{ onExited: onExited }}
              {...dialogProps}
            >
              {FormComponent && (
                <FormComponent
                  rowId={rowId}
                  rowData={rowData}
                  handleClose={handleClose}
                  {...additionalFormProps}
                />
              )}
            </Dialog>
          )}
          <div className={classes.dataGridContainer}>
            <DataGrid<T>
              {...props}
              shouldRenderToolBar={shouldRenderToolBar}
              rows={defaultTo([], rows)}
              columns={columns}
              pagination
              {...(isContentRangePagination && {
                paginationMode: 'server',
                rowCount,
                page,
                onPageChange: (newPage, details) =>
                  onPageChange?.(newPage, details),
                onPageSizeChange: (newPageSize, details) =>
                  onPageSizeChange?.(newPageSize, details)
              })}
              groupingColDef={groupingColDef}
              pageSize={pageSize}
              initialState={initialState}
              disableActions={disableActions}
              rowsPerPageOptions={
                isContentRangePagination ? [25, 50, 100] : [50]
              }
              defaultGroupingExpansionDepth={collapse}
              {...(editable && {
                onEditClick: (id, row) => handleEditClick(id, row)
              })}
              isCellEditable={() => false}
              isRowSelectable={() => false}
              canBeEditedProp={canBeEditedProp}
              canBeDeletedProp={canBeDeletedProp}
              autoHeight={autoHeight}
              headerContent={props.headerContent}
              {...(!hideCreateAction ? { onAddClick: handleAddClick } : {})}
              {...(customActions
                ? {
                    customActions: getCustomActions
                  }
                : '')}
            />
          </div>
        </>
      )}
    </>
  );
};

TableCRUDLayout.displayName = 'TableCRUDLayout';

export default TableCRUDLayout;
